import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Plus, Clock, CheckCircle2, AlertCircle, X, Send, Paperclip } from 'lucide-react';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { dbSupportTickets, dbTicketReplies, SupabaseTicket, SupabaseTicketReply } from '../../utils/supabase';
import { compressImageToBase64 } from '../../utils/api';
interface SupportTicketsProps {
  userEmail: string;
  userName: string;
  userPhone: string;
  instanceName: string;
  language: string;
  isRTL: boolean;
}
export function SupportTickets({
  userEmail,
  userName,
  userPhone,
  instanceName,
  language,
  isRTL
}: SupportTicketsProps) {
  const {
    showToast
  } = useToast();
  const [tickets, setTickets] = useState<SupabaseTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Ticket Detail State
  const [selectedTicket, setSelectedTicket] = useState<SupabaseTicket | null>(null);
  const [ticketReplies, setTicketReplies] = useState<SupabaseTicketReply[]>([]);
  const [isLoadingReplies, setIsLoadingReplies] = useState(false);
  // Form state
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  // Reply state
  const [replyMessage, setReplyMessage] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [attachmentBase64, setAttachmentBase64] = useState<string | null>(null);
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await compressImageToBase64(file);
      setAttachmentBase64(base64);
    } catch (error) {
      console.error('Failed to compress image:', error);
      showToast('error', isRTL ? 'فشل تحميل الصورة' : 'Failed to load image');
    }
  };
  const handleReplyTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicket || !replyMessage.trim() && !attachmentBase64) return;
    setIsReplying(true);
    try {
      let finalMessage = replyMessage;
      if (attachmentBase64) {
        finalMessage += `\n\n[ATTACHMENT:${attachmentBase64}]`;
      }
      const reply = await dbTicketReplies.create({
        ticket_id: selectedTicket.id!,
        from_type: 'user',
        from_name: userName,
        message: finalMessage
      });
      if (reply) {
        setTicketReplies([...ticketReplies, reply]);
        setReplyMessage('');
        setAttachmentBase64(null);
        showToast('success', isRTL ? 'تم إرسال الرد' : 'Reply sent');
      }
    } catch (error) {
      console.error('Reply error:', error);
      showToast('error', isRTL ? 'فشل إرسال الرد' : 'Failed to send reply');
    } finally {
      setIsReplying(false);
    }
  };
  const renderMessageContent = (msg: string) => {
    const parts = msg.split('[ATTACHMENT:');
    if (parts.length === 1) return <p className="whitespace-pre-wrap" data-id="element-261">{msg}</p>;
    const textPart = parts[0].trim();
    const attachmentPart = parts[1].split(']')[0];
    return <div className="space-y-2" data-id="element-262">
        {textPart && <p className="whitespace-pre-wrap" data-id="element-263">{textPart}</p>}
        {attachmentPart && <img src={attachmentPart} alt="Attachment" className="max-w-full h-auto rounded-lg border border-white/20 max-h-64 object-contain" data-id="element-264" />}
      </div>;
  };
  useEffect(() => {
    loadTickets();
  }, [userEmail]);
  const loadTickets = async () => {
    setIsLoading(true);
    // Load from localStorage first (instant)
    const stored = JSON.parse(localStorage.getItem('operines_support_tickets') || '[]');
    if (stored.length > 0) {
      setTickets(stored);
    }
    // Then try Supabase (sync)
    try {
      const data = await dbSupportTickets.getAll(userEmail);
      if (data.length > 0) {
        // Fetch replies for all tickets to check if they have been replied to
        const ticketIds = data.map(t => t.id).filter(Boolean) as string[];
        const allReplies = await dbTicketReplies.getByTicketIds(ticketIds);
        const ticketsWithReplies = data.map(ticket => {
          const hasAdminReply = allReplies.some(r => r.ticket_id === ticket.id && r.from_type === 'admin');
          return {
            ...ticket,
            has_admin_reply: hasAdminReply
          };
        });
        setTickets(ticketsWithReplies);
        localStorage.setItem('operines_support_tickets', JSON.stringify(ticketsWithReplies));
      }
    } catch (error) {
      console.warn('Supabase load failed:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleTicketClick = async (ticket: SupabaseTicket) => {
    setSelectedTicket(ticket);
    setIsLoadingReplies(true);
    try {
      const replies = await dbTicketReplies.getByTicket(ticket.id!);
      setTicketReplies(replies);
    } catch (error) {
      console.error('Failed to load replies:', error);
    } finally {
      setIsLoadingReplies(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim() && !attachmentBase64) {
      showToast('error', isRTL ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    setIsSubmitting(true);
    try {
      let finalMessage = message;
      if (attachmentBase64) {
        finalMessage += `\n\n[ATTACHMENT:${attachmentBase64}]`;
      }
      const ticketId = crypto.randomUUID?.() || Date.now().toString();
      const now = new Date().toISOString();
      // 1. Try saving to Supabase (non-blocking)
      let savedTicket: SupabaseTicket | null = null;
      try {
        savedTicket = await dbSupportTickets.create({
          user_email: userEmail,
          subject,
          message: finalMessage,
          priority,
          status: 'open'
        });
      } catch (e) {
        console.warn('Supabase save failed (table may not exist):', e);
      }
      // 2. Build the ticket object (use Supabase data if available, otherwise local)
      const ticket: SupabaseTicket = savedTicket || {
        id: ticketId,
        user_email: userEmail,
        subject,
        message: finalMessage,
        priority,
        status: 'open',
        created_at: now,
        updated_at: now
      };
      // 3. Send to n8n webhook
      const webhookPayload = {
        ticketId: ticket.id,
        userEmail,
        userName,
        userPhone,
        instanceName,
        subject,
        message: finalMessage,
        priority,
        status: 'open',
        createdAt: now
      };
      fetch('https://n8n.operines.me/webhook/support-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(webhookPayload)
      }).catch(() => {
        fetch('https://n8n.operines.me/webhook/support-ticket', {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(webhookPayload)
        }).catch(console.error);
      });
      // 4. Save to localStorage
      const stored = JSON.parse(localStorage.getItem('operines_support_tickets') || '[]');
      localStorage.setItem('operines_support_tickets', JSON.stringify([ticket, ...stored]));
      // 5. Update UI
      setTickets([ticket, ...tickets]);
      setIsModalOpen(false);
      setSubject('');
      setMessage('');
      setPriority('medium');
      setAttachmentBase64(null);
      showToast('success', isRTL ? 'تم إرسال التذكرة بنجاح' : 'Ticket submitted successfully');
    } catch (error) {
      console.error('Submit ticket error:', error);
      showToast('error', isRTL ? 'حدث خطأ أثناء إرسال التذكرة' : 'Failed to submit ticket');
    } finally {
      setIsSubmitting(false);
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-265">
            <AlertCircle className="w-3.5 h-3.5" data-id="element-266" />
            {isRTL ? 'مفتوحة' : 'Open'}
          </span>;
      case 'in_progress':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-267">
            <Clock className="w-3.5 h-3.5" data-id="element-268" />
            {isRTL ? 'قيد المعالجة' : 'In Progress'}
          </span>;
      case 'resolved':
      case 'closed':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-269">
            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-270" />
            {isRTL ? 'مغلقة' : 'Closed'}
          </span>;
      default:
        return null;
    }
  };
  const getPriorityBadge = (p: string) => {
    switch (p) {
      case 'high':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-600 border border-red-100" data-id="element-271">
            {isRTL ? 'عاجل' : 'High'}
          </span>;
      case 'medium':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-600 border border-orange-100" data-id="element-272">
            {isRTL ? 'متوسط' : 'Medium'}
          </span>;
      case 'low':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200" data-id="element-273">
            {isRTL ? 'عادي' : 'Low'}
          </span>;
      default:
        return null;
    }
  };
  // --- Render Detail View ---
  if (selectedTicket) {
    return <div className="space-y-6 max-w-4xl mx-auto" data-id="element-274">
        <button onClick={() => setSelectedTicket(null)} className="text-gray-500 hover:text-[#4f1964] flex items-center gap-2 text-sm font-medium" data-id="element-275">
          <X className="w-4 h-4" data-id="element-276" />
          {isRTL ? 'العودة للقائمة' : 'Back to list'}
        </button>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-277">
          <div className="p-6 border-b border-gray-100" data-id="element-278">
            <div className="flex justify-between items-start mb-4" data-id="element-279">
              <div data-id="element-280">
                <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-281">
                  {selectedTicket.subject}
                </h2>
                <div className="flex items-center gap-3 text-sm text-gray-500" data-id="element-282">
                  <span data-id="element-283">
                    {new Date(selectedTicket.created_at!).toLocaleString()}
                  </span>
                  <span data-id="element-284">•</span>
                  <span data-id="element-285">ID: {selectedTicket.id?.split('-')[0]}</span>
                </div>
              </div>
              <div className="flex items-center gap-3" data-id="element-286">
                {getPriorityBadge(selectedTicket.priority)}
                {getStatusBadge(selectedTicket.status)}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-gray-700 whitespace-pre-wrap" data-id="element-287">
              {renderMessageContent(selectedTicket.message)}
            </div>
          </div>

          <div className="p-6 bg-gray-50 space-y-4 min-h-[200px]" data-id="element-288">
            <h3 className="font-bold text-gray-900" data-id="element-289">
              {isRTL ? 'الردود' : 'Replies'}
            </h3>

            {isLoadingReplies ? <div className="flex justify-center py-8" data-id="element-290">
                <div className="w-6 h-6 border-2 border-[#4f1964]/30 border-t-[#4f1964] rounded-full animate-spin" data-id="element-291" />
              </div> : ticketReplies.length === 0 ? <p className="text-sm text-gray-500 text-center py-8" data-id="element-292">
                {isRTL ? 'لا توجد ردود حتى الآن' : 'No replies yet'}
              </p> : <div className="space-y-4" data-id="element-293">
                {ticketReplies.map(reply => <div key={reply.id} className={`flex ${reply.from_type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-294">
                    <div className={`max-w-[80%] rounded-2xl p-4 ${reply.from_type === 'user' ? 'bg-[#4f1964] text-white rounded-tr-none' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none shadow-sm'}`} data-id="element-295">
                      <div className="flex items-center gap-2 mb-1 opacity-80 text-xs" data-id="element-296">
                        <span className="font-bold" data-id="element-297">
                          {reply.from_type === 'admin' ? `${isRTL ? 'فريق الدعم' : 'Support'} — ${reply.from_name}` : reply.from_name}
                        </span>
                        <span data-id="element-298">•</span>
                        <span data-id="element-299">
                          {new Date(reply.created_at!).toLocaleString()}
                        </span>
                      </div>
                      {renderMessageContent(reply.message)}
                    </div>
                  </div>)}
              </div>}

            {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && <form onSubmit={handleReplyTicket} className="mt-6 pt-6 border-t border-gray-200" data-id="element-300">
                  <div className="relative" data-id="element-301">
                    <textarea value={replyMessage} onChange={e => setReplyMessage(e.target.value)} placeholder={isRTL ? 'اكتب ردك هنا...' : 'Type your reply...'} className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] outline-none resize-none mb-3" rows={3} data-id="element-302" />
                    <div className="absolute top-3 right-3" data-id="element-303">
                      <label className="cursor-pointer text-gray-400 hover:text-[#4f1964] transition-colors p-1 block" data-id="element-304">
                        <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} data-id="element-305" />
                        <Paperclip className="w-5 h-5" data-id="element-306" />
                      </label>
                    </div>
                  </div>

                  {attachmentBase64 && <div className="mb-4 relative inline-block" data-id="element-307">
                      <img src={attachmentBase64} alt="Preview" className="h-20 w-auto rounded border border-gray-200 object-cover" data-id="element-308" />
                      <button type="button" onClick={() => setAttachmentBase64(null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600" data-id="element-309">
                        <X className="w-3 h-3" data-id="element-310" />
                      </button>
                    </div>}

                  <div className="flex justify-end" data-id="element-311">
                    <Button type="submit" className="bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center gap-2" disabled={isReplying || !replyMessage.trim() && !attachmentBase64} data-id="element-312">
                      {isReplying ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-313" /> : <Send className="w-4 h-4" data-id="element-314" />}
                      {isRTL ? 'إرسال الرد' : 'Send Reply'}
                    </Button>
                  </div>
                </form>}
            {(selectedTicket.status === 'closed' || selectedTicket.status === 'resolved') && <div className="mt-6 p-4 bg-gray-100 rounded-lg text-center text-sm text-gray-500" data-id="element-315">
                {isRTL ? 'هذه التذكرة مغلقة ولا يمكن الرد عليها' : 'This ticket is closed and cannot be replied to'}
              </div>}
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-316">
      <div className="flex justify-between items-center" data-id="element-317">
        <div data-id="element-318">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-319">
            {isRTL ? 'الدعم الفني' : 'Support Tickets'}
          </h2>
          <p className="text-sm text-gray-500 mt-1" data-id="element-320">
            {isRTL ? 'تواصل مع فريق الدعم لحل أي مشكلة' : 'Contact our support team for help'}
          </p>
        </div>
        <Button onClick={() => {
        setAttachmentBase64(null);
        setIsModalOpen(true);
      }} className="bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center gap-2" data-id="element-321">
          <Plus className="w-4 h-4" data-id="element-322" />
          {isRTL ? 'تذكرة جديدة' : 'New Ticket'}
        </Button>
      </div>

      {/* Tickets List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-323">
        {isLoading ? <div className="p-8 text-center text-gray-500" data-id="element-324">
            <div className="animate-spin w-8 h-8 border-4 border-[#4f1964] border-t-transparent rounded-full mx-auto mb-4" data-id="element-325" />
            {isRTL ? 'جاري التحميل...' : 'Loading...'}
          </div> : tickets.length === 0 ? <div className="p-12 text-center flex flex-col items-center" data-id="element-326">
            <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-4" data-id="element-327">
              <MessageSquare className="w-8 h-8 text-[#4f1964]" data-id="element-328" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-329">
              {isRTL ? 'لا توجد تذاكر' : 'No tickets yet'}
            </h3>
            <p className="text-gray-500 max-w-sm mx-auto" data-id="element-330">
              {isRTL ? 'لم تقم بإنشاء أي تذاكر دعم فني بعد. انقر على الزر أعلاه لإنشاء تذكرة جديدة.' : "You haven't created any support tickets yet. Click the button above to create one."}
            </p>
          </div> : <div className="divide-y divide-gray-100" data-id="element-331">
            {tickets.map(ticket => <div key={ticket.id} onClick={() => handleTicketClick(ticket)} className="p-5 hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-332">
                <div className="flex justify-between items-start gap-4" data-id="element-333">
                  <div className="flex-1 min-w-0" data-id="element-334">
                    <div className="flex items-center gap-3 mb-1" data-id="element-335">
                      <h4 className="text-base font-semibold text-gray-900 truncate" data-id="element-336">
                        {ticket.subject}
                      </h4>
                      {getPriorityBadge(ticket.priority)}
                      {ticket.has_admin_reply && <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-600 border border-purple-100" data-id="element-337">
                          {isRTL ? 'تم الرد' : 'Replied'}
                        </span>}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3" data-id="element-338">
                      {ticket.message.split('[ATTACHMENT:')[0].trim() || (isRTL ? 'مرفق' : 'Attachment')}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-339">
                      <span data-id="element-340">
                        {new Date(ticket.created_at!).toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </span>
                      <span className="text-gray-300" data-id="element-341">•</span>
                      <span className="font-mono" data-id="element-342">
                        ID: {ticket.id?.split('-')[0]}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0" data-id="element-343">
                    {getStatusBadge(ticket.status)}
                  </div>
                </div>
              </div>)}
          </div>}
      </div>

      {/* Create Ticket Modal */}
      <AnimatePresence data-id="element-344">
        {isModalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !isSubmitting && setIsModalOpen(false)} data-id="element-345">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden" data-id="element-346">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-347">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-348">
                  {isRTL ? 'إنشاء تذكرة دعم' : 'Create Support Ticket'}
                </h3>
                <button onClick={() => !isSubmitting && setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors" disabled={isSubmitting} data-id="element-349">
                  <X className="w-5 h-5" data-id="element-350" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-351">
                <div data-id="element-352">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-353">
                    {isRTL ? 'الموضوع' : 'Subject'}
                  </label>
                  <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder={isRTL ? 'مثال: مشكلة في ربط واتساب' : 'e.g., Issue connecting WhatsApp'} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] focus:border-transparent outline-none transition-all" required disabled={isSubmitting} data-id="element-354" />
                </div>

                <div data-id="element-355">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-356">
                    {isRTL ? 'الأهمية' : 'Priority'}
                  </label>
                  <div className="grid grid-cols-3 gap-3" data-id="element-357">
                    {[{
                  id: 'low',
                  label: isRTL ? 'عادي' : 'Low',
                  color: 'gray'
                }, {
                  id: 'medium',
                  label: isRTL ? 'متوسط' : 'Medium',
                  color: 'orange'
                }, {
                  id: 'high',
                  label: isRTL ? 'عاجل' : 'High',
                  color: 'red'
                }].map(p => <button key={p.id} type="button" onClick={() => setPriority(p.id as any)} disabled={isSubmitting} className={`py-2 px-3 rounded-lg text-sm font-medium border transition-all ${priority === p.id ? p.color === 'red' ? 'bg-red-50 border-red-200 text-red-700' : p.color === 'orange' ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-gray-100 border-gray-300 text-gray-900' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-358">
                        {p.label}
                      </button>)}
                  </div>
                </div>

                <div data-id="element-359">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-360">
                    {isRTL ? 'التفاصيل' : 'Details'}
                  </label>
                  <div className="relative" data-id="element-361">
                    <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder={isRTL ? 'اشرح المشكلة بالتفصيل...' : 'Describe the issue in detail...'} rows={5} className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] focus:border-transparent outline-none transition-all resize-none" required={!attachmentBase64} disabled={isSubmitting} data-id="element-362" />
                    <div className="absolute top-3 right-3" data-id="element-363">
                      <label className="cursor-pointer text-gray-400 hover:text-[#4f1964] transition-colors p-1 block" data-id="element-364">
                        <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} disabled={isSubmitting} data-id="element-365" />
                        <Paperclip className="w-5 h-5" data-id="element-366" />
                      </label>
                    </div>
                  </div>

                  {attachmentBase64 && <div className="mt-3 relative inline-block" data-id="element-367">
                      <img src={attachmentBase64} alt="Preview" className="h-20 w-auto rounded border border-gray-200 object-cover" data-id="element-368" />
                      <button type="button" onClick={() => setAttachmentBase64(null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600" disabled={isSubmitting} data-id="element-369">
                        <X className="w-3 h-3" data-id="element-370" />
                      </button>
                    </div>}
                </div>

                <div className="pt-2 flex gap-3" data-id="element-371">
                  <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1" disabled={isSubmitting} data-id="element-372">
                    {isRTL ? 'إلغاء' : 'Cancel'}
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#4f1964] text-white hover:bg-[#3d1350] flex items-center justify-center gap-2" disabled={isSubmitting} data-id="element-373">
                    {isSubmitting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-374" /> : <>
                        <Send className="w-4 h-4" data-id="element-375" />
                        {isRTL ? 'إرسال' : 'Submit'}
                      </>}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}